import { Component, Inject } from "@angular/core";
import { NavParams } from "ionic-angular/navigation/nav-params";
import { NavController } from "ionic-angular/navigation/nav-controller";
import {
  DiaprofilPatient,
  IDiaprofilPatient
} from "../../app/models/diaprofil-patient";
import {
  PatientHistory,
  DIAPROFIL_QUESTIONNAIRE_UUID
} from "../../app/models/types";
import { DiaprofilProTask } from "../../app/models/diaprofil-pro-task";
import { TranslateService } from "@ngx-translate/core";
import { QuestionnaireResponseService } from "@zitelab/ionic-fhir-questionnaires";
import { DashboardService } from "../../app/services/dashboard.service";
import { ModalController } from "ionic-angular/components/modal/modal-controller";
import { ManageTaskModal } from "../../modals/manage-task.modal/manage-task.modal";
import { ManagePatientModal } from "../../modals/manage-patient.modal/manage-patient.modal";
import { AdminService } from "../../app/services/admin.service";
import { DashboardPage } from "../dashboard.page/dashboard.page";
import { FhirService } from "../../app/services/fhir.service";
import { Store } from "@ngrx/store";
import { AppState } from "../../app/models/app.state";
import { Subscription } from "rxjs/Subscription";
import { OnDestroy } from "@angular/core/src/metadata/lifecycle_hooks";
import { KihImportModal } from "../../modals/kih-import.modal/kih-import.modal";
import { TaskLogModal } from "../../modals/task-log.modal/task-log.modal";
import { PatientChartsModal } from "../../modals/patient-charts.modal/patient-charts.modal";
import {
  EnvironmentVariables,
  ENV_VARS
} from "../../app/environment-variables/environment-variables.module";
import {
  EventLoggingService,
  LogEventId
} from "../../app/services/event-logging.service";
import { LogEventListingPage } from "../log-event-listing.page/log-event-listing.page";
import { NotificationLogModal } from "../../modals/notification-log.modal/notification-log.modal";

@Component({
  selector: "patient-page",
  templateUrl: "patient.page.html"
})
export class PatientPage implements OnDestroy {
  patient: DiaprofilPatient = null;
  patientName: string = null;
  patientHistory: PatientHistory = [];
  proTasks: DiaprofilProTask[] = [];
  completeProTasks: DiaprofilProTask[] = [];
  incompleteProTasks: DiaprofilProTask[] = [];
  activeProTask: DiaprofilProTask = null;
  patientsSub: Subscription;
  constructor(
    private navCtrl: NavController,
    private navParams: NavParams,
    private translate: TranslateService,
    private questionnaireResponseService: QuestionnaireResponseService,
    private fhir: FhirService,
    private dashboard: DashboardService,
    public admin: AdminService,
    private modalCtrl: ModalController,
    private store: Store<AppState>,
    private eventLogging: EventLoggingService,
    @Inject(ENV_VARS) public envVars: EnvironmentVariables
  ) {
    this.patient = this.navParams.get("patient");
    this.setCoreValues();
    this.patientsSub = this.store
      .select(state => state.patients)
      .subscribe(patients => {
        const rawPatient = patients.find(
          elem => elem.fhirId === this.patient.fhirId
        );
        this.patient = new DiaprofilPatient(
          rawPatient,
          this.translate,
          this.questionnaireResponseService,
          this.dashboard
        );
        this.setCoreValues();
      });
  }

  ionViewWillEnter() {
    this.admin.getUsers({
      userType: "Patient",
      organizationUuid: this.admin.userState.organizationUuid,
      fhirId: this.patient.fhirId,
      extraAction: users => {
        this.admin.addPatientsToStore(users);
      }
    });
    this.eventLogging.saveEvent({
      event_id: LogEventId.OPEN_PATIENT_PAGE,
      event_src_location: "PatientPage",
      patient_fhir_id: this.patient.fhirId,
      patient_full_name: this.patient.fullName
    });
  }

  goToLogEventListingPage() {
    this.navCtrl.push(LogEventListingPage, { patient: this.patient });
  }

  setCoreValues() {
    this.patientName = this.patient.fullName;
    this.patientHistory = this.patient.patientHistory;
    console.log(this.patient.proTasks);
    this.proTasks = this.patient.proTasks.map(
      proTask =>
        new DiaprofilProTask(
          proTask,
          this.translate,
          this.questionnaireResponseService,
          this.dashboard,
          this.patient
        )
    );
    this.completeProTasks = this.proTasks.filter(
      proTask =>
        proTask.response !== null && proTask.consultationStatus.handled !== null && proTask.taskDetailedStatus !== "CANCELLED"
    );
    this.incompleteProTasks = this.proTasks.filter(
      proTask =>
        proTask.response !== null && proTask.consultationStatus.handled === null && proTask.taskDetailedStatus !== "CANCELLED"
    );
    this.activeProTask = this.getProTaskForActiveTask(this.patient.activeTask);
  }

  ngOnDestroy() {
    this.patientsSub.unsubscribe();
  }

  triggerTaskModal(patient: DiaprofilPatient, task = null) {
    const modal = this.modalCtrl.create(
      ManageTaskModal,
      { patient, task, user: this.admin.userState },
      { cssClass: "medium-modal" }
    );
    modal.present();
  }

  handleExistingTaskEdit($event) {
    const taskResource = $event.proTask.task;
    this.triggerTaskModal(this.patient, taskResource);
  }

  triggerManagePatientModal($event, patient?: IDiaprofilPatient) {
    const navParams =
      patient !== undefined
        ? { patient, modalType: "EDIT" }
        : { modalType: "CREATE" };
    const modal = this.modalCtrl.create(ManagePatientModal, navParams, {
      cssClass: "large-modal",
      enableBackdropDismiss: false
    });
    modal.present();
  }

  triggerKihImportModal($event) {
    const modal = this.modalCtrl.create(
      KihImportModal,
      { patient: this.patient },
      {
        cssClass: "large-modal",
        enableBackdropDismiss: false
      }
    );
    modal.present();
  }

  triggerTaskLogModal($event) {
    const modal = this.modalCtrl.create(
      TaskLogModal,
      { patient: this.patient },
      {
        cssClass: "large-modal",
        enableBackdropDismiss: false
      }
    );
    modal.present();
  }

  triggerChartsModal($event) {
    const modal = this.modalCtrl.create(
      PatientChartsModal,
      { patient: this.patient },
      {
        cssClass: "large-modal",
        enableBackdropDismiss: false
      }
    );
    modal.present();
  }

  toDashboardPage($event) {
    if (this.dashboard.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.dashboard.setGenericProWidgets(questionnaireResource);
    }
    const navParams = {
      prevPageName: "PatientPage",
      responseResource: $event.proTask.response,
      proTask: $event.proTask,
      patient: this.patient
    };
    this.navCtrl.push(DashboardPage, navParams, { animate: false });
  }

  getProTaskForActiveTask(activeTask: fhir.Task): DiaprofilProTask {
    if (this.patient.activeTask === null) {
      return null;
    }
    const activeProTask =
      this.proTasks.find(
        proTask => proTask.task.id === this.patient.activeTask.id
      ) || null;
    return activeProTask;
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient.page/patient.page.ts